import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Calculator, Globe2, Linkedin, Scale, ShieldCheck, Users } from "lucide-react";
import Reveal from "@/components/Reveal";

const ABOUT_IMG = "/founder.jpg";

const PILLARS = [
  { icon: ShieldCheck, title: "Confidencialidad absoluta", desc: "Tu información nunca sale del despacho. Ni antes, ni durante, ni después de la operación." },
  { icon: Users, title: "Trato de socio a socio", desc: "Hablamos directamente contigo, sin intermediarios ni equipos impersonales." },
  { icon: Globe2, title: "Alcance internacional", desc: "Trabajamos con compradores estratégicos e inversores dentro y fuera de España." },
];

export default function About() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [-30, 30]);

  return (
    <section id="nosotros" data-testid="about-section" ref={ref} className="relative border-y border-gold/10 bg-ink-2 py-24 md:py-36">
      <div className="mx-auto grid max-w-7xl gap-16 px-6 md:px-10 lg:grid-cols-2 lg:items-center">
        <div>
          <Reveal testid="about-image" className="relative">
            <div className="absolute -left-4 -top-4 hidden h-full w-full border border-gold/40 md:block" />
            <div className="relative overflow-hidden">
              <motion.img
                style={{ y: yImg }}
                src={ABOUT_IMG}
                alt="Jesús López Paños, socio director de AJG M&A Consulting"
                className="h-[480px] w-full scale-110 object-cover object-top md:h-[620px]"
                data-testid="about-founder-photo"
              />
              <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between gap-3">
                <div className="flex items-center gap-3 bg-ink/85 px-5 py-3 backdrop-blur-sm" data-testid="about-founder-badge">
                  <span className="h-2 w-2 rotate-45 bg-gold" />
                  <div>
                    <p className="font-serif text-lg leading-tight text-white">Jesús López Paños</p>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-gold">
                      Socio Director · M&amp;A
                    </p>
                  </div>
                </div>
                <a
                  href="https://www.linkedin.com/in/jesus-lopez-finanzas/"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="about-linkedin-link"
                  aria-label="LinkedIn de Jesús López Paños"
                  className="flex h-11 w-11 shrink-0 items-center justify-center border border-gold/60 bg-ink/85 text-gold backdrop-blur-sm transition-all duration-300 hover:bg-gold hover:text-ink"
                >
                  <Linkedin className="h-5 w-5" />
                </a>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.15} testid="about-collaborators" className="mt-10">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gold">Colaboradores externos</p>
            <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-3">
              {[
                { label: "Jurídico-Mercantil", testid: "collaborator-juridico", icon: Scale },
                { label: "Fiscal", testid: "collaborator-fiscal", icon: Calculator },
              ].map(({ label, testid, icon: Icon }) => (
                <div
                  key={label}
                  data-testid={testid}
                  className="flex flex-col items-start gap-4 border border-gold/25 bg-ink/60 px-6 py-6 transition-colors duration-300 hover:border-gold/60"
                >
                  <Icon className="h-6 w-6 text-gold" />
                  <span className="text-sm font-semibold leading-snug tracking-wide text-slate-100">{label}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>

        <div>
          <Reveal>
            <p className="eyebrow">Nosotros</p>
            <h2 className="mt-6 font-serif text-4xl leading-tight text-white sm:text-5xl">
              Una boutique de M&amp;A al servicio del empresario.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 text-base leading-relaxed text-slate-300">
              AJG M&amp;A Consulting, S.L. nace para acompañar a los empresarios en uno de los momentos más
              importantes de su vida profesional: la venta de su empresa. Combinamos rigor financiero y jurídico con
              un trato cercano, transparente y estratégico.
            </p>
            <p className="mt-5 text-base leading-relaxed text-slate-300" data-testid="about-bio-1">
              Profesionales con más de seis años de experiencia en consultoría empresarial, combinando su carrera
              como consultores con experiencia en fondos de inversión. Su trayectoria se ha centrado en la valoración
              de empresas y en procesos corporativos de M&amp;A en sectores diversos: residuos, tratamiento de aguas,
              sector deportivo y sector del transporte, entre otros.
            </p>
            <p className="mt-5 text-base leading-relaxed text-slate-300" data-testid="about-bio-2">
              Hemos participado en <strong className="font-semibold text-gold">más de 15 operaciones de compraventa</strong>,
              acumulando experiencia en transacciones de hasta{" "}
              <strong className="font-semibold text-gold">100 millones de euros</strong>, de las cuales hemos liderado
              proyectos por valor de <strong className="font-semibold text-gold">45 millones de euros acumulados</strong>.
            </p>
            <p className="mt-5 text-base leading-relaxed text-slate-300">
              Cada mandato es único. Por eso trabajamos con un número limitado de clientes y con un compromiso
              absoluto de confidencialidad desde la primera conversación.
            </p>
          </Reveal>
          <div className="mt-12 space-y-8">
            {PILLARS.map(({ icon: Icon, title, desc }, i) => (
              <Reveal key={title} delay={0.15 + i * 0.08} testid={`about-pillar-${i + 1}`}>
                <div className="flex gap-5">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center border border-gold/40">
                    <Icon className="h-5 w-5 text-gold" />
                  </span>
                  <div>
                    <h3 className="font-serif text-xl text-white">{title}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-slate-400">{desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
